import { useSyncExternalStore } from 'react'

import { useOnline, useOutboxAnzahl } from '../hooks/useOutbox'
import { absagenBeobachten, absageWeg, letzteAbsage } from '../lib/absagen'
import { XIcon } from './Icons'
import { useTexte } from '../texte'

/**
 * Ein Streifen, wenn nichts durchgeht.
 *
 * **Er steht innerhalb der Anwendung, nicht am Rand des Fensters.** Die erste
 * Version saß ganz oben über allem — und in einem Safari-Tab liegt dort die
 * durchscheinende Browserleiste darüber. Übrig blieb ein blasses Band mit
 * unlesbarer Schrift: ein Hinweis, den der Browser verdeckt, ist keiner.
 *
 * Deshalb steht er jetzt im Chat **unmittelbar über der Eingabezeile**, also
 * dort, wo die Folge eintritt: wer schreibt, liest ihn im selben Blick. In der
 * Liste, wo es nichts zu schreiben gibt, sitzt er unter der Kopfzeile.
 *
 * Wartet etwas, steht die Zahl dabei. „Kein Netz" allein wäre eine Diagnose;
 * „2 warten" ist eine Auskunft.
 *
 * **Und seit 1.34.0 trägt er die Absagen mit** (Codereview E2). Er steht
 * ohnehin auf allen zehn Seiten; eine elfte Stelle für dieselbe Sache wäre
 * genau die Doppelung, die hier sonst überall eingesammelt wird. Die Absage
 * kommt aus `lib/absagen.ts`, wo `MutationCache.onError` sie hinterlegt.
 */
export function Netzstreifen({
  /**
   * Ob hier überhaupt etwas nachkommt (Codereview E6).
   *
   * Der Chat, die Karte und die Fotoseite haben einen Ausgangskorb — dort
   * stimmt „geht später von selbst raus". Die Info-Seite, die Einstellungen
   * und eine neue Reise haben keinen, und dort war der Satz ein Versprechen,
   * das niemand einlöst.
   */
  ausgangskorb = true,
}: {
  ausgangskorb?: boolean
} = {}) {
  const t = useTexte().bausteine.netz
  const online = useOnline()
  const wartend = useOutboxAnzahl()
  const absage = useSyncExternalStore(absagenBeobachten, letzteAbsage, letzteAbsage)

  if (online && absage === null) {
    return null
  }

  return (
    <>
      {!online && (
        <p
          className="netzstreifen"
          role="status"
          /* Welche der beiden Varianten hier steht (Codereview E6) -- ein
             Prüflauf hängt an der Marke, nicht am Wortlaut, und auf einer
             Seite mit Chatliste stehen zwei Streifen nebeneinander. */
          data-netz={ausgangskorb ? 'korb' : 'ohne'}
        >
          {t.kein}
          {/* „Eintrag" und nicht „Nachricht": im Korb liegen seit den Highlights
              zwei Arten von Wartendem, und „1 Nachricht wartet" wäre bei einem
              Punkt auf der Karte schlicht falsch. */}
          {wartend > 0 ? t.wartend(wartend) : ausgangskorb ? t.leer : t.nichtsWartet}
        </p>
      )}
      {absage !== null && (
        <p
          className="netzstreifen netzstreifen--absage"
          role="alert"
          /* Dieselbe Marke wie oben, und aus demselben Grund: auf einer Seite
             mit Chatliste stehen zwei Streifen im DOM, und einer davon ist
             unsichtbar. Ein Prüflauf, der den falschen greift, wartet auf
             etwas, das nie sichtbar wird. */
          data-absage={ausgangskorb ? 'korb' : 'ohne'}
        >
          <span className="netzstreifen__text">{absage}</span>
          <button
            type="button"
            className="netzstreifen__weg"
            aria-label={t.absageWeg}
            onClick={absageWeg}
          >
            <XIcon size={14} />
          </button>
        </p>
      )}
    </>
  )
}
